import React from "react";
import { useNavigate } from "react-router-dom";
import {
  UserPlusIcon,
  UsersIcon,
  TargetIcon,
  BellIcon,
  CheckIcon,
  XIcon,
  ClockIcon,
  TrophyIcon,
} from "lucide-react";
import { Button } from "../ui/button";
import { Notification } from "../../lib/supabase";

interface NotificationItemProps {
  notification: Notification;
  onAcceptFriendRequest?: (
    notificationId: string,
    friendRequestId: string
  ) => void;
  onRejectFriendRequest?: (
    notificationId: string,
    friendRequestId: string
  ) => void;
  onMarkAsRead?: (notificationId: string) => void;
}

/**
 * Komponen ini menampilkan satu item notifikasi,
 * dengan ikon, teks, waktu, dan tombol aksi (jika diperlukan).
 */
export const NotificationItem: React.FC<NotificationItemProps> = ({
  notification,
  onAcceptFriendRequest,
  onRejectFriendRequest,
  onMarkAsRead,
}) => {
  const navigate = useNavigate();

  // Pilih ikon berdasarkan tipe notifikasi
  const getNotificationIcon = () => {
    switch (notification.type) {
      case "friend_request":
        return UserPlusIcon;
      case "collaboration_invite":
        return UsersIcon;
      case "assessment_invite":
        return UsersIcon;
      case "challenge_reminder":
        return TargetIcon;
      case "challenge_completed":
        return TrophyIcon;
      case "system_announcement":
        return BellIcon;
      default:
        return BellIcon;
    }
  };

  // Warna ikon berdasarkan tipe notifikasi
  const getNotificationIconColor = () => {
    switch (notification.type) {
      case "friend_request":
        return "bg-blue-100 text-blue-600";
      case "collaboration_invite":
        return "bg-purple-100 text-purple-600";
      case "assessment_invite":
        return "bg-yellow-100 text-yellow-600";
      case "challenge_reminder":
        return "bg-orange-100 text-orange-600";
      case "challenge_completed":
        return "bg-green-100 text-green-600";
      default:
        return "bg-gray-100 text-gray-600";
    }
  };

  // Format waktu relatif
  const formatTimestamp = (timestamp: string) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diffInMinutes = Math.floor(
      (now.getTime() - date.getTime()) / (1000 * 60)
    );

    if (diffInMinutes < 1) return "Baru saja";
    if (diffInMinutes < 60) return `${diffInMinutes} menit lalu`;
    const diffInHours = Math.floor(diffInMinutes / 60);
    if (diffInHours < 24) return `${diffInHours} jam lalu`;
    const diffInDays = Math.floor(diffInHours / 24);
    if (diffInDays < 7) return `${diffInDays} hari lalu`;
    return date.toLocaleDateString("id-ID", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  // Klik pada notifikasi → navigasi + tandai sebagai read
  const handleNotificationClick = () => {
    if (notification.related_entity_id) {
      if (notification.type === "collaboration_invite") {
        navigate(`/lesson/${notification.related_entity_id}`);
      } else if (notification.type === "assessment_invite") {
        navigate(`/assessment/${notification.related_entity_id}`);
      } else if (notification.type === "challenge_reminder") {
        navigate("/gamification");
      }
    }

    if (!notification.is_read && onMarkAsRead) {
      onMarkAsRead(notification.id);
    }
  };

  // Terima permintaan teman
  const handleAccept = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (notification.related_entity_id && onAcceptFriendRequest) {
      onAcceptFriendRequest(notification.id, notification.related_entity_id);
    }
  };

  // Tolak permintaan teman
  const handleReject = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (notification.related_entity_id && onRejectFriendRequest) {
      onRejectFriendRequest(notification.id, notification.related_entity_id);
    }
  };

  const Icon = getNotificationIcon();
  const iconColorClass = getNotificationIconColor();

  return (
    <div
      onClick={handleNotificationClick}
      className={`p-3 border-b border-gray-100 hover:bg-gray-50 transition-colors duration-200 cursor-pointer ${
        !notification.is_read ? "bg-blue-50 border-l-4 border-l-blue-500" : ""
      }`}
    >
      <div className="flex items-start gap-3">
        {/* Ikon */}
        <div
          className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${iconColorClass}`}
        >
          <Icon className="w-4 h-4" />
        </div>

        {/* Isi notifikasi */}
        <div className="flex-1 min-w-0">
          <h4 className="font-medium text-[#373e46] text-xs mb-1">
            {notification.title}
          </h4>
          <p className="text-xs text-gray-600 leading-relaxed mb-2">
            {notification.content}
          </p>

          <div className="flex items-center gap-2 text-xs text-gray-500">
            <ClockIcon className="w-3 h-3" />
            <span>{formatTimestamp(notification.created_at)}</span>
            {!notification.is_read && (
              <span className="w-2 h-2 bg-blue-500 rounded-full ml-2"></span>
            )}
          </div>

          {/* Tombol aksi untuk friend_request */}
          {notification.type === "friend_request" &&
            notification.action_status === "pending" && (
              <div className="flex items-center gap-2 mt-2">
                <Button
                  onClick={handleAccept}
                  size="sm"
                  className="bg-green-600 hover:bg-green-700 text-white px-2 py-1 h-6 text-xs rounded-lg transition-all duration-300"
                >
                  <CheckIcon className="w-3 h-3 mr-1" />
                  Terima
                </Button>
                <Button
                  onClick={handleReject}
                  size="sm"
                  variant="outline"
                  className="border-red-300 text-red-600 hover:bg-red-50 px-2 py-1 h-6 text-xs rounded-lg transition-all duration-300"
                >
                  <XIcon className="w-3 h-3 mr-1" />
                  Tolak
                </Button>
              </div>
            )}
        </div>
      </div>
    </div>
  );
};
